/**
 * 部分敏感模块仅允许以下角色（与 getInfo.data.roles、UmsRole.name 一致）
 *
 * 与《管理后台-兑换积分相关角色与权限说明》对照：
 * - 细粒度能力角色（id 10～14） + 兼容旧角色名须与 ums_role 一致
 * - 积分侧栏：INTEGRATION_MODULE_ROLE_NAMES
 * - 核销：VERIFY_MODULE_ROLE_NAMES
 */
import {
  FINE_GIFT_INTEGRATION_ADMIN,
  FINE_GIFT_VERIFY_CARD_ADMIN,
  FINE_GIFT_VERIFY_CARD_ACTIVATE_ADMIN,
  FINE_INTEGRATION_ACTIVATION_ADMIN,
  FINE_INTEGRATION_STATS_ADMIN,
  FINE_MEMBER_REGISTER_ADMIN,
  FINE_SUPPLIER_ADMIN,
  FINE_VERIFY_CARD_ADMIN,
  INTEGRATION_MANAGER_ROLE_NAME,
  SYSTEM_ADMIN_ROLE_NAME,
  VERIFICATION_ADMIN_ROLE_NAME
} from './integrationModuleScopeConfig'

/**
 * 将 /admin/info 返回的 roles 统一为角色名字符串数组（去空格、兼容 { name } 对象）
 */
export function normalizeRoleList(roles) {
  if (!roles || !Array.isArray(roles)) {
    return []
  }
  return roles
    .map((r) => {
      if (r == null) return ''
      if (typeof r === 'string') return r.trim()
      if (typeof r === 'object') {
        const name = r.name != null ? r.name : (r.roleName != null ? r.roleName : (r.title != null ? r.title : r.role))
        return name != null ? String(name).trim() : ''
      }
      return String(r).trim()
    })
    .filter(Boolean)
}

export const PRIVILEGED_ROLE_NAMES = ['超级管理员', '系统管理员']

/** 与 getInfo.data.roles、UmsRole.name 一致，仅角色名为「超级管理员」 */
export const SUPER_ADMIN_ROLE_NAME = '超级管理员'

/** 与 ums_role.name 一致 */
export const ORDER_ADMIN_ROLE_NAME = '订单管理员'

export function hasSuperAdminRole(roles) {
  const list = normalizeRoleList(roles)
  return list.indexOf(SUPER_ADMIN_ROLE_NAME) !== -1
}

export function hasOrderAdminRole(roles) {
  const list = normalizeRoleList(roles)
  return list.indexOf(ORDER_ADMIN_ROLE_NAME) !== -1
}

/** 订单详情线下换货：超级管理员或订单管理员 */
export function canOfflineExchangeOrder(roles) {
  return hasSuperAdminRole(roles) || hasOrderAdminRole(roles)
}

/**
 * 兑换积分批次「更正为订单退回」（POST …/reclassify-order-refund）前端准入：
 * **仅** `ums_role.name === 超级管理员`，不含系统管理员等（《管理后台-开发API文档》§4.4）。
 */
export function canInnerBatchReclassifyOrderRefund(roles) {
  return hasSuperAdminRole(roles)
}

/**
 * 经销商侧后台账号（`ums_role.name` 与 getInfo.roles 一致）。
 * 用于兑换积分汇总等：经销商仍展示「默认个人」选项与全 0 行，与厂端管理员区分。
 */
export function hasDealerAdminRole(roles) {
  const list = normalizeRoleList(roles)
  return list.some((n) => n === '经销商' || n.indexOf('经销商') !== -1)
}

export function hasPrivilegedAdminRole(roles) {
  const list = normalizeRoleList(roles)
  if (list.length === 0) return false
  return list.some((r) => PRIVILEGED_ROLE_NAMES.indexOf(r) !== -1)
}

/** 积分管理侧栏（/integration…）；含细粒度 ②③⑤⑥ 与旧角色 */
export const INTEGRATION_MODULE_ROLE_NAMES = [
  '超级管理员',
  '系统管理员',
  '积分管理员',
  FINE_GIFT_INTEGRATION_ADMIN,
  FINE_GIFT_VERIFY_CARD_ADMIN,
  FINE_INTEGRATION_ACTIVATION_ADMIN,
  FINE_INTEGRATION_STATS_ADMIN
]

export function hasIntegrationModuleRole(roles) {
  const list = normalizeRoleList(roles)
  if (list.length === 0) return false
  return list.some((r) => INTEGRATION_MODULE_ROLE_NAMES.indexOf(r) !== -1)
}

/**
 * 因「积分管理」侧栏而展示 **会员** 模块时，须具备的角色（不含仅 ⑤ 积分激活管理员）。
 * 与《管理后台-兑换积分相关角色与权限说明》一致：⑤ 不展示「会员管理」。
 */
export const INTEGRATION_ROLES_FOR_MEMBER_SIDEBAR = INTEGRATION_MODULE_ROLE_NAMES.filter(
  (r) => r !== FINE_INTEGRATION_ACTIVATION_ADMIN
)

/** 可访问「兑换积分批次」子菜单：② 与 ⑤ 能力范围与侧栏产品一致；超管/系统/积分管理员/赠送积分/积分激活 */
const INNER_GIFT_BATCH_PAGE_ROLE_NAMES = [
  SUPER_ADMIN_ROLE_NAME,
  SYSTEM_ADMIN_ROLE_NAME,
  INTEGRATION_MANAGER_ROLE_NAME,
  FINE_GIFT_INTEGRATION_ADMIN,
  FINE_INTEGRATION_ACTIVATION_ADMIN
]

export function hasInnerGiftBatchPageRole(roles) {
  const list = normalizeRoleList(roles)
  if (list.length === 0) return false
  return list.some((r) => INNER_GIFT_BATCH_PAGE_ROLE_NAMES.indexOf(r) !== -1)
}

/**
 * 兑换积分批次页 /integration/inner-gift-batch：②⑤ 原有角色，或 ⑥ 积分统计（报表统计权限，只读查批次）。
 */
export function hasInnerGiftBatchPageAccess(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles) || hasPrivilegedAdminRole(roles)) {
    return true
  }
  if (hasInnerGiftBatchPageRole(roles)) {
    return true
  }
  return hasInnerIntegrationStatsReportAccess(roles, integrationModuleScope)
}

/**
 * 兑换积分批次列表：激活 / 冻结 / 解冻 —— 仅积分激活管理员（⑤）、积分管理员（兼容），或超管/系统管；⑤ 能力 scope 为 true 时同权。
 * 不含仅 ② 赠送积分管理员等。
 */
export function hasInnerGiftBatchActivateFreezeAccess(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles) || hasPrivilegedAdminRole(roles)) {
    return true
  }
  const list = normalizeRoleList(roles)
  if (list.indexOf(INTEGRATION_MANAGER_ROLE_NAME) !== -1) return true
  if (list.indexOf(FINE_INTEGRATION_ACTIVATION_ADMIN) !== -1) return true
  const scope = integrationModuleScope
  if (scope && scope.integrationActivationAllowed === true) return true
  return false
}

/**
 * 可查看「兑换积分流水」所对应的角色：② 赠送积分 / 旧「积分管理员」等。
 * 「赠送卡券管理员」单角色不含此项（与 scope.giftIntegrationAllowed 一致）。
 */
const GIFT_INTEGRATION_FLOW_PAGE_ROLE_NAMES = [
  SUPER_ADMIN_ROLE_NAME,
  SYSTEM_ADMIN_ROLE_NAME,
  INTEGRATION_MANAGER_ROLE_NAME,
  FINE_GIFT_INTEGRATION_ADMIN
]

export function hasGiftIntegrationFlowPageRole(roles) {
  const list = normalizeRoleList(roles)
  if (list.length === 0) return false
  return list.some((r) => GIFT_INTEGRATION_FLOW_PAGE_ROLE_NAMES.indexOf(r) !== -1)
}

/**
 * 积分统计报表/明细菜单与路由：积分管理员、积分统计管理员（⑥），或 `integrationStatsAllowed`；
 * 超级管理员、系统管理员（与接口资源说明一致）。不含仅 ②③⑤ 等角色。
 */
export function hasInnerIntegrationStatsReportAccess(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles) || hasPrivilegedAdminRole(roles)) {
    return true
  }
  const list = normalizeRoleList(roles)
  if (list.indexOf(INTEGRATION_MANAGER_ROLE_NAME) !== -1) return true
  if (list.indexOf(FINE_INTEGRATION_STATS_ADMIN) !== -1) return true
  const scope = integrationModuleScope
  if (scope && scope.integrationStatsAllowed === true) return true
  return false
}

/**
 * 核销卡券列表「导出 Excel」：须具备 **积分统计管理员**（⑥）角色；超级管理员例外。
 */
export function hasVerifyCardListExportAccess(roles) {
  if (hasSuperAdminRole(roles)) return true
  const list = normalizeRoleList(roles)
  return list.indexOf(FINE_INTEGRATION_STATS_ADMIN) !== -1
}

/**
 * 会员列表「加赠送兑换积分」：与 ② 赠送积分能力一致
 *（超级管理员、系统管理员、积分管理员、赠送积分管理员）；不含仅 ①③④⑤ 等细粒度角色。
 */
export function hasMemberListGiftInnerIntegrationAction(roles) {
  return hasGiftIntegrationFlowPageRole(roles)
}

/**
 * 兑换积分批次「扣减」：与 ② 赠送积分能力一致（超级/系统/积分管理员、赠送积分管理员）。
 */
export function hasInnerBatchDeductAccess(roles, integrationModuleScope) {
  if (hasMemberListGiftInnerIntegrationAction(roles)) {
    return true
  }
  const scope = integrationModuleScope
  return scope && scope.giftIntegrationAllowed === true
}

/** 公司管理模块（与 getInfo.data.roles、UmsRole.name 一致） */
export const COMPANY_MODULE_ROLE_NAMES = ['公司管理员', '系统管理员', '超级管理员']

export function hasCompanyModuleRole(roles) {
  const list = normalizeRoleList(roles)
  if (list.length === 0) return false
  return list.some((r) => COMPANY_MODULE_ROLE_NAMES.indexOf(r) !== -1)
}

/**
 * 核销侧应用内拦截 `VerificationAdminRoleInterceptor` 允许的角色
 *（与 `VerificationAdminRoleConstants.ALLOWED_ROLE_NAMES` 一致：含积分管理员、不含「系统管理员」字面角色）
 */
export const VERIFY_MODULE_ROLE_NAMES = [
  '超级管理员',
  '核销管理员',
  '积分管理员',
  FINE_VERIFY_CARD_ADMIN
]

export function hasVerifyModuleRole(roles) {
  const list = normalizeRoleList(roles)
  if (list.length === 0) return false
  return list.some((r) => VERIFY_MODULE_ROLE_NAMES.indexOf(r) !== -1)
}

/**
 * 管理赠送核销卡券激活（⑦）POST /member-verify-card/activate-admin-gift
 * 《管理后台-赠送卡券与订单相关API》§3.5：仅超级管理员、赠送卡券激活管理员。
 */
export function hasAdminGiftVerifyCardActivateAccess(roles) {
  if (hasSuperAdminRole(roles)) return true
  const list = normalizeRoleList(roles)
  return list.indexOf(FINE_GIFT_VERIFY_CARD_ACTIVATE_ADMIN) !== -1
}

/**
 * 可进入「核销管理」页 /verify/manage：须为 **核销管理员** 或 **卡券核销管理员**，或超级/系统管理员。
 * 不含仅「积分管理员」；列表与到店核销等仍用 hasVerifyModuleRole。
 */
export function hasVerifyManagePageAccess(roles) {
  if (hasSuperAdminRole(roles) || hasPrivilegedAdminRole(roles)) return true
  const list = normalizeRoleList(roles)
  if (list.indexOf(VERIFICATION_ADMIN_ROLE_NAME) !== -1) return true
  if (list.indexOf(FINE_VERIFY_CARD_ADMIN) !== -1) return true
  return false
}

/**
 * 侧栏与路由：在 hasVerifyManagePageAccess 基础上尊重 `integrationModuleScope.verifyCardAllowed`（超管/系统管不受限）。
 */
export function canAccessVerifyManage(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles) || hasPrivilegedAdminRole(roles)) return true
  if (!hasVerifyManagePageAccess(roles)) return false
  const scope = integrationModuleScope
  if (scope && scope.verifyCardAllowed !== undefined) return !!scope.verifyCardAllowed
  return true
}

/**
 * 可打开「核销卡券列表」/verify/card-list：核销模块角色，或 ③ 赠送卡券 / ⑦ 赠送卡券激活。
 * 超级管理员、系统管理员与 hasVerifyModuleRole 一致可进；不含仅 ③/⑦ 时对「核销管理」扫码页的权限。
 */
export function hasVerifyCardListAccess(roles, integrationModuleScope) {
  if (hasVerifyModuleRole(roles)) return true
  if (hasSuperAdminRole(roles)) return true
  if (hasPrivilegedAdminRole(roles)) return true
  const list = normalizeRoleList(roles)
  if (list.indexOf(FINE_GIFT_VERIFY_CARD_ADMIN) !== -1) return true
  if (list.indexOf(FINE_GIFT_VERIFY_CARD_ACTIVATE_ADMIN) !== -1) return true
  const scope = integrationModuleScope
  if (scope && scope.giftVerifyCardAllowed === true) return true
  if (scope && scope.giftVerifyCardActivationAllowed === true) return true
  return false
}

export const MEMBER_MODULE_ROLE_NAME = '会员管理员'

/**
 * 会员侧栏：含「会员注册管理员」或「会员管理员」；公司/超管/系统；或
 * 因积分类角色进入（**不含**仅「积分激活管理员」：⑤ 不展示会员管理，见产品说明）
 */
export function hasMemberModuleRole(roles) {
  const list = normalizeRoleList(roles)
  if (list.indexOf(FINE_MEMBER_REGISTER_ADMIN) !== -1 || list.indexOf(MEMBER_MODULE_ROLE_NAME) !== -1) {
    return true
  }
  if (hasCompanyModuleRole(roles) || hasPrivilegedAdminRole(roles)) {
    return true
  }
  return list.some((r) => INTEGRATION_ROLES_FOR_MEMBER_SIDEBAR.indexOf(r) !== -1)
}

/**
 * 是否可进入「会员」侧栏及 /member/**（除仅注册页需另看 memberRegisterAllowed）。
 * ① 会员注册、② 赠送积分、③ 赠送卡券 任一 scope 为 true 即可查看会员列表；① 单独为 false 不再整模块封死。
 * scope 不全或无时回退 hasMemberModuleRole。
 */
export function hasMemberModuleAccess(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles)) {
    return true
  }
  const scope = integrationModuleScope
  if (scope && typeof scope === 'object') {
    if (scope.memberRegisterAllowed === true) return true
    if (scope.giftIntegrationAllowed === true) return true
    if (scope.giftVerifyCardAllowed === true) return true
    if (
      scope.memberRegisterAllowed === false &&
      scope.giftIntegrationAllowed !== true &&
      scope.giftVerifyCardAllowed !== true
    ) {
      return false
    }
  }
  return hasMemberModuleRole(roles)
}

/**
 * 是否可打开「注册会员」页 /member/register（① memberRegisterAllowed 或与侧栏 register 一致的角色兜底）。
 */
export function hasMemberRegisterRouteAccess(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles)) return true
  const scope = integrationModuleScope
  if (scope && scope.memberRegisterAllowed !== undefined) {
    return !!scope.memberRegisterAllowed
  }
  return hasMemberModuleRole(roles)
}

/**
 * 是否为「会员注册」角色/① 能力（不含超级管理员）。
 */
export function hasMemberRegisterAdminRole(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles)) return false
  const list = normalizeRoleList(roles)
  if (list.indexOf(FINE_MEMBER_REGISTER_ADMIN) !== -1) return true
  const scope = integrationModuleScope
  return !!(scope && scope.memberRegisterAllowed === true)
}

/**
 * 会员注册角色：无关键字时列表不展示普通会员；有关键字时可检索普通会员。
 */
export function shouldHideOrdinaryMembersWithoutKeyword(roles, integrationModuleScope) {
  return hasMemberRegisterAdminRole(roles, integrationModuleScope)
}

/**
 * 会员列表「查看」「编辑」：会员管理员、会员注册管理员（角色或 ① memberRegisterAllowed）；超级管理员始终可。
 * 仅 ②③ 等角色可进列表但不可改会员档案。
 */
export function hasMemberListViewEditAccess(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles)) return true
  const list = normalizeRoleList(roles)
  if (list.indexOf(MEMBER_MODULE_ROLE_NAME) !== -1) return true
  if (list.indexOf(FINE_MEMBER_REGISTER_ADMIN) !== -1) return true
  const scope = integrationModuleScope
  if (scope && scope.memberRegisterAllowed === true) return true
  return false
}

/**
 * 会员列表身份证列与导出：仅 **会员管理员**、**会员注册管理员**、超级管理员（角色名）；不含仅凭 ① scope 而无上述角色者。
 */
export function hasMemberListIdCardColumnAccess(roles) {
  if (hasSuperAdminRole(roles)) return true
  const list = normalizeRoleList(roles)
  if (list.indexOf(MEMBER_MODULE_ROLE_NAME) !== -1) return true
  if (list.indexOf(FINE_MEMBER_REGISTER_ADMIN) !== -1) return true
  return false
}

/** ⑧ 商品供应商管理（`/supplier/**`）：超级管理员、供应商管理员，或 scope.supplierManagementAllowed */
export const SUPPLIER_ADMIN_ROLE_NAME = FINE_SUPPLIER_ADMIN

export function hasSupplierManagementAccess(roles, integrationModuleScope) {
  if (hasSuperAdminRole(roles)) return true
  const list = normalizeRoleList(roles)
  if (list.indexOf(SUPPLIER_ADMIN_ROLE_NAME) !== -1) return true
  const scope = integrationModuleScope
  if (scope && scope.supplierManagementAllowed === true) return true
  return false
}

/** 是否已注册「商品」模块（商品管理员等仍可从供应商一级菜单进产品页） */
export function hasPmsModuleInRoutes(routers) {
  if (!Array.isArray(routers)) return false
  const pms = routers.find((r) => r && r.name === 'pms')
  return !!(pms && pms.hidden !== true)
}

/** 一级「供应商」侧栏/路由：供应商管理员 或 已具备商品模块 */
export function canAccessSupplierModule(roles, integrationModuleScope, routers) {
  if (hasSuperAdminRole(roles)) return true
  if (hasSupplierManagementAccess(roles, integrationModuleScope)) return true
  return hasPmsModuleInRoutes(routers)
}

/** 供应商产品/回填子页：与一级模块相同；供应商管理页仍须 hasSupplierManagementAccess */
export function canAccessSupplierProductPages(roles, integrationModuleScope, routers) {
  return canAccessSupplierModule(roles, integrationModuleScope, routers)
}

/** @deprecated 使用 hasPrivilegedAdminRole */
export const canAccessPyScriptTool = hasPrivilegedAdminRole



// WEBPACK FOOTER //
// ./src/utils/privilegedRoleAccess.js